import type { ClubRole, FlowDefinition } from "@rivaled/shared";
import { createFileRoute, Link, useBlocker, useNavigate } from "@tanstack/react-router";
import { Loader2, Lock, Workflow } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { FeatureGate } from "../../../../../../components/FeatureGate";
import { Breadcrumbs, Button } from "../../../../../../components/ui";
import { usePlatformRole } from "../../../../../../hooks/usePlatformRole";
import { canClubRolePermission } from "../../../../../../lib/permissions";
import { trpc } from "../../../../../../lib/trpc";
import { FlowEditor } from "../../../../../../match-flow/editor/FlowEditor";

export const Route = createFileRoute("/app/clubs/$slug/queues/$queueSlug/flow-editor")({
  component: FlowEditorPage,
});

function FlowEditorPage() {
  const { slug, queueSlug } = Route.useParams();
  const navigate = useNavigate();
  const { isLoading: isPlatformRoleLoading, isModerator: isPlatformModerator } = usePlatformRole();

  const { data: myClubs, isLoading: myClubsLoading } = trpc.club.getMyClubs.useQuery();
  const { data: club, isLoading: clubLoading } = trpc.club.get.useQuery({ slug });
  const membership = myClubs?.find((c) => c.slug === slug);
  const clubId = membership?.id ?? club?.id;
  const isAdmin =
    canClubRolePermission(membership?.role as ClubRole | undefined, {
      resource: "flow",
      actions: ["update"],
    }) || isPlatformModerator;

  const { data: queue, isLoading: queueLoading } = trpc.queue.get.useQuery({
    clubSlug: slug,
    queueSlug,
  });
  const queueId = queue?.id ?? "";

  // Load existing flow definition if the queue has one
  const { data: flowDefinition, isLoading: flowLoading } = trpc.matchFlow.get.useQuery(
    { flowId: queue?.matchFlowDefinitionId ?? "", clubId: clubId ?? "" },
    { enabled: !!queue?.matchFlowDefinitionId && !!clubId },
  );

  // List existing flow definitions for the club
  const utils = trpc.useUtils();
  const [isSaving, setIsSaving] = useState(false);
  const [editorIsDirty, setEditorIsDirty] = useState(false);
  const [saveVersion, setSaveVersion] = useState(0);

  useBlocker({
    condition: editorIsDirty,
    blockerFn: () => window.confirm("You have unsaved changes. Are you sure you want to leave?"),
  });

  // Create new flow definition
  const createMutation = trpc.matchFlow.create.useMutation({
    onSuccess: (data) => {
      void utils.matchFlow.list.invalidate();
      // Associate with queue
      assignFlowToQueue(data.id);
    },
    onError: (err) => {
      toast.error(err.message || "Failed to create flow");
      setIsSaving(false);
    },
  });

  // Update existing flow definition
  const updateMutation = trpc.matchFlow.update.useMutation({
    onSuccess: () => {
      toast.success("Flow saved");
      void utils.matchFlow.get.invalidate();
      void utils.matchFlow.list.invalidate();
      setIsSaving(false);
      setSaveVersion((v) => v + 1);
    },
    onError: (err) => {
      toast.error(err.message || "Failed to save flow");
      setIsSaving(false);
    },
  });

  // Update queue to use the flow definition
  const updateQueueMutation = trpc.queue.update.useMutation({
    onSuccess: () => {
      toast.success("Flow saved and assigned to queue");
      void utils.queue.get.invalidate({ queueId });
      setIsSaving(false);
      setSaveVersion((v) => v + 1);
    },
    onError: (err) => {
      toast.error(err.message || "Failed to assign flow to queue");
      setIsSaving(false);
    },
  });

  const assignFlowToQueue = (flowId: string) => {
    if (!clubId) return;
    updateQueueMutation.mutate({
      queueId,
      clubId,
      matchFlowDefinitionId: flowId,
    });
  };

  const handleSave = (flow: FlowDefinition) => {
    if (!clubId) return;
    setIsSaving(true);

    if (queue?.matchFlowDefinitionId && flowDefinition) {
      // Update existing flow
      updateMutation.mutate({
        flowId: queue.matchFlowDefinitionId,
        clubId,
        flowData: flow,
      });
    } else {
      // Create new flow
      createMutation.mutate({
        clubId,
        name: `${queue?.name ?? "Queue"} Flow`,
        description: `Match flow for ${queue?.name ?? "this queue"}`,
        flowData: flow,
      });
    }
  };

  // Access check - redirect if not admin
  if (
    !queueLoading &&
    !myClubsLoading &&
    !clubLoading &&
    !isPlatformRoleLoading &&
    (!isAdmin || !clubId)
  ) {
    void navigate({ to: "/app/clubs/$slug/queues/$queueSlug", params: { slug, queueSlug } });
    return null;
  }

  if (queueLoading || myClubsLoading || clubLoading || isPlatformRoleLoading || !queue) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-900">
        <Loader2 className="size-8 animate-spin text-surface-400" />
      </div>
    );
  }

  // Wait for flow to load if queue has one
  if (queue.matchFlowDefinitionId && flowLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-900">
        <Loader2 className="size-8 animate-spin text-surface-400" />
      </div>
    );
  }

  return (
    <FeatureGate
      clubId={clubId!}
      slug={slug}
      feature="hasVisualEditor"
      fallback={
        <div className="h-full flex flex-col items-center justify-center bg-surface-900 p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-surface-800 border border-surface-700 flex items-center justify-center mb-4">
            <Lock className="size-8 text-surface-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Visual Flow Editor</h1>
          <p className="text-surface-400 max-w-md mb-6">
            The visual flow editor is available on Competitive tier and above. Upgrade your club to
            create custom match flows.
          </p>
          <div className="flex gap-3">
            <Link to="/app/clubs/$slug/queues/$queueSlug" params={{ slug, queueSlug }}>
              <Button variant="ghost">Back to Queue</Button>
            </Link>
            <Link to="/app/clubs/$slug/settings" params={{ slug }} search={{ section: "billing" }}>
              <Button variant="primary">Upgrade Plan</Button>
            </Link>
          </div>
        </div>
      }
    >
      <div className="h-full flex flex-col bg-surface-900 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-700 bg-surface-800 shrink-0">
          <div className="flex items-center gap-3">
            <Workflow className="size-5 text-brand-400" />
            <div>
              <Breadcrumbs
                items={[
                  { label: membership!.name, to: "/app/clubs/$slug", params: { slug } },
                  {
                    label: queue.name,
                    to: "/app/clubs/$slug/queues/$queueSlug",
                    params: { slug, queueSlug },
                  },
                  { label: "Flow Editor" },
                ]}
                className="text-xs"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isSaving && (
              <span className="text-xs text-surface-400 flex items-center gap-1.5">
                <Loader2 className="size-3.5 animate-spin" />
                Saving...
              </span>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 min-h-0">
          <FlowEditor
            clubId={clubId!}
            queueId={queueId}
            flowId={queue.matchFlowDefinitionId ?? undefined}
            initialFlow={flowDefinition?.flowData as FlowDefinition | undefined}
            onSave={handleSave}
            saveVersion={saveVersion}
            onDirtyChange={setEditorIsDirty}
          />
        </div>
      </div>
    </FeatureGate>
  );
}
